// L02 · Traffic Lights, But (SPEC §3, Act I).
// A classic 3×3 grid — "select all squares containing traffic lights" — where the tiles
// quietly interrogate what "a traffic light" even is: a drawing of one, a toy one, a
// reflection in a puddle, the emoji, a dead one, a stop sign, grass. Any selection passes;
// the machine comments on your ontology (local table in content/voice.ts).
//
// Traits (§3): `poet` if you counted a representation as the thing; `literalist` if you
// counted only the ones physically, presently there.

import { el } from "@/ui/chrome.ts";
import { runLevel, promptEl, buildGrid, type Tile } from "@/levels/util.ts";
import { L02 as V, type VoiceLine } from "@/content/voice.ts";
import type { Level, LevelContext, LevelResult } from "@/levels/types.ts";

// Tile order matters — the verdict logic references these indices by role.
const TILES: Tile[] = [
  { emoji: "🚦", cap: "on the corner" }, // 0 real
  { emoji: "🚥", cap: "at the intersection" }, // 1 real
  { emoji: "🎨", cap: "a child drew this" }, // 2 drawing
  { emoji: "🧸", cap: "a toy, from a train set" }, // 3 toy
  { emoji: "💧", cap: "reflected in a puddle" }, // 4 reflection
  { emoji: "🔤", cap: "the emoji 🚦" }, // 5 glyph
  { emoji: "🌿", cap: "grass" }, // 6 grass
  { emoji: "🛑", cap: "a stop sign" }, // 7 stop
  { emoji: "🌑", cap: "one that is switched off" }, // 8 dead
];

const DRAWING = 2;
const TOY = 3;
const REFLECTION = 4;
const GLYPH = 5;
const STOP = 7;

/** Map a selection set to a verdict-table key (§3 inclusion pattern). */
function verdict(sel: Set<number>): VoiceLine {
  if (sel.size === 0) return V.empty!;
  if (sel.size === TILES.length) return V.all!;
  // A representation was counted as the thing → poet. Most pointed one wins the line.
  if (sel.has(DRAWING)) return V.drawing!;
  if (sel.has(REFLECTION)) return V.reflection!;
  if (sel.has(GLYPH)) return V.glyph!;
  if (sel.has(TOY)) return V.toy!;
  if (sel.has(STOP)) return V.stop!;
  // Only the ones that are really, presently there → literalist.
  return V.literal!;
}

export const L02: Level = {
  id: "L02",
  act: 1,
  title: "Traffic Lights, But",

  mount(host: HTMLElement, ctx: LevelContext): Promise<LevelResult> {
    return runLevel(ctx, (done) => {
      host.append(promptEl("Select all squares containing traffic lights."));
      const { root, selected } = buildGrid(TILES);
      host.append(root);

      const verify = el("button", "btn");
      verify.textContent = "Verify";
      verify.addEventListener("click", () => {
        const { line, traits } = verdict(selected);
        done({ pass: true, line, traits });
      });
      host.append(verify);
    });
  },
};
